// ============================================================================
// "Dial it in together" — design reference screen 6. The account-manager card
// comes first, for everyone (an executive reads who checked it before touching
// a slider). Then: what the sub will take it for, our margin, OR nights. The
// price is the OUTPUT of sub and margin — the database computes it; nothing
// here types a price. The grid and summary come from bid_quote_summary().
// Both people on a call see the same numbers: the screen re-reads every 5 s.
// ============================================================================

import { useEffect, useState } from 'react';
import { Button, Skeleton } from '../../shared/components';
import { fetchQuoteSummary, priceBid, updateBid, type BidRow, type PriceLines, type QuoteSummary } from './api';
import { useSavedPrice } from './price-lines';
import { AmReviewCard } from './am-review-card';
import { Field, INPUT, SaveError, Section } from './fields';
import { saveText } from './start-form';
import { money, pct, perSf, shortDate } from './format';
import { useBidLoad } from './use-bid-load';

interface GridCell { sub: number; margin: number; price: number; current: boolean; below_floor: boolean }

function DialIn({ bid, summary, locked }: { bid: BidRow; summary: QuoteSummary; locked: boolean }) {
  const [sub, setSub] = useState(String(bid.sub_cost_assumed ?? ''));
  const [margin, setMargin] = useState(String(bid.margin_assumed ?? ''));
  const [orNights, setOrNights] = useState(String(bid.or_terminal_nights_per_week ?? bid.service_freq_per_week ?? ''));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setSub(String(bid.sub_cost_assumed ?? ''));
    setMargin(String(bid.margin_assumed ?? ''));
    setOrNights(String(bid.or_terminal_nights_per_week ?? bid.service_freq_per_week ?? ''));
  }, [bid.sub_cost_assumed, bid.margin_assumed, bid.or_terminal_nights_per_week, bid.service_freq_per_week]);

  const changed = sub !== String(bid.sub_cost_assumed ?? '') || margin !== String(bid.margin_assumed ?? '')
    || orNights !== String(bid.or_terminal_nights_per_week ?? bid.service_freq_per_week ?? '');
  const surgery = bid.facility_type !== 'Dialysis';

  async function apply() {
    setSaving(true); setErr(null);
    try {
      const orChanged = surgery && orNights !== String(bid.or_terminal_nights_per_week ?? bid.service_freq_per_week ?? '');
      if (orChanged) {
        // OR nights change what the sub will take the building for, so the three
        // numbers are priced again and the sub cost starts from the new estimate;
        // the margin the two of you chose is kept.
        await updateBid(bid.bid_id, { or_terminal_nights_per_week: Number(orNights) }, 'change the OR nights');
        await priceBid(bid.bid_id, true);
        await updateBid(bid.bid_id, { margin_assumed: Number(margin) }, 'use these numbers');
      } else {
        await updateBid(bid.bid_id, { sub_cost_assumed: Number(sub), margin_assumed: Number(margin) }, 'use these numbers');
      }
    } catch (e) { setErr(saveText(e)); } finally { setSaving(false); }
  }

  const grid = Array.isArray(summary.grid) ? (summary.grid as unknown as GridCell[]) : [];
  const margins = grid.slice(0, 3).map((g) => g.margin);

  return (
    <Section title="Dial it in">
      <SaveError text={err} />
      <div className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between text-sm"><span className="font-medium">What the sub will take it for</span><span className="tabular-nums">{money(sub)} a month</span></div>
          <input type="range" className="w-full" disabled={locked || summary.sub_slider_min == null}
            min={summary.sub_slider_min ?? 0} max={summary.sub_slider_max ?? 0} step={10}
            value={sub || 0} onChange={(e) => setSub(e.target.value)} aria-label="What the sub will take it for" />
          <div className="flex justify-between text-xs text-tcs-text-muted">
            <span>{summary.sub_ref_low != null ? `book's low ${money(summary.sub_ref_low)}` : ''}</span>
            <span>{summary.sub_ref_median != null ? `book's middle ${money(summary.sub_ref_median)}${summary.sub_ref_label ? ` (${summary.sub_ref_label})` : ''}` : 'no sub costs in the book for buildings like this yet'}</span>
            <span>{summary.sub_ref_high != null ? `book's high ${money(summary.sub_ref_high)}` : ''}</span>
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between text-sm"><span className="font-medium">Our margin</span><span className="tabular-nums">{pct(margin, 1)}</span></div>
          <input type="range" className="w-full" disabled={locked} min={summary.margin_slider_min ?? 0} max={summary.margin_slider_max ?? 0.4} step={0.005}
            value={margin || 0} onChange={(e) => setMargin(e.target.value)} aria-label="Our margin" />
          <div className="flex justify-between text-xs text-tcs-text-muted"><span>{pct(summary.margin_slider_min)} floor</span><span>{pct(summary.margin_slider_max)}</span></div>
        </div>
        {surgery && (
          <Field label="OR terminal nights a week">
            <select className={INPUT} value={orNights} disabled={locked} onChange={(e) => setOrNights(e.target.value)}>
              {Array.from({ length: bid.service_freq_per_week ?? 7 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
        )}
        {!locked && (
          <div className="flex items-center justify-end gap-3">
            {changed && <span className="text-xs text-tcs-text-muted">{surgery && orNights !== String(bid.or_terminal_nights_per_week ?? bid.service_freq_per_week ?? '') ? 'Changing the OR nights prices the building again and starts the sub cost from the new estimate.' : 'The price updates when you use these numbers.'}</span>}
            <Button variant="secondary" size="sm" disabled={saving} onClick={async () => { setSaving(true); setErr(null); try { await priceBid(bid.bid_id, true); } catch (e) { setErr(saveText(e)); } finally { setSaving(false); } }}>Back to the suggestion</Button>
            <Button size="sm" onClick={apply} disabled={!changed} loading={saving}>Use these numbers</Button>
          </div>
        )}
        {grid.length === 9 && (
          <div className="overflow-x-auto">
            <table className="w-full text-center text-sm tabular-nums">
              <thead className="text-xs text-tcs-text-muted">
                <tr><th className="px-2 py-1 text-left">If the sub costs… / and we want…</th>{margins.map((m) => <th key={m} className="px-2 py-1">{pct(m, 1)}</th>)}</tr>
              </thead>
              <tbody>
                {[0, 1, 2].map((i) => (
                  <tr key={i} className="border-t border-tcs-border">
                    <td className="px-2 py-1 text-left">{money(grid[i * 3].sub)}</td>
                    {grid.slice(i * 3, i * 3 + 3).map((g) => (
                      <td key={`${g.sub}-${g.margin}`} className={`px-2 py-1 ${g.current ? 'font-semibold text-tcs-text' : 'text-tcs-text-secondary'} ${g.below_floor ? 'text-st-danger-fg' : ''}`}>{money(g.price)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Section>
  );
}

function Summary({ bid, summary, price }: { bid: BidRow; summary: QuoteSummary; price: PriceLines | null }) {
  const row = (k: string, v: string) => (
    <div className="flex justify-between border-t border-tcs-border py-1.5 text-sm"><span className="text-tcs-text-muted">{k}</span><span className="tabular-nums">{v}</span></div>
  );
  // the monthly price is nightly cleaning plus what step 3 adds (Ben, 2026-10-15, #841);
  // the per-sf figures and the margin stay the nightly line's, as before
  const added = Number(price?.consumables_monthly ?? 0) + Number(price?.floor_care_monthly ?? 0) > 0;
  return (
    <div className="rounded-xl bg-tcs-inverse p-4 text-tcs-inverse-ink">
      <div className="text-xs opacity-80">Monthly</div>
      <div className="text-3xl font-semibold tabular-nums">{money(price?.total_monthly ?? summary.quoted_monthly)}</div>
      {added && (
        <div className="text-xs opacity-80">
          {[`Nightly cleaning ${money(summary.quoted_monthly)}`,
            Number(price?.consumables_monthly ?? 0) > 0 ? `consumables ${money(price?.consumables_monthly)}` : null,
            Number(price?.floor_care_monthly ?? 0) > 0 ? `periodic floor care ${money(price?.floor_care_monthly)}` : null].filter(Boolean).join(' · ')}
        </div>
      )}
      <div className="mb-2 text-xs opacity-80">
        {[summary.quoted_per_sf != null ? `nightly ${perSf(summary.quoted_per_sf)} / sf` : null,
          summary.quoted_per_or != null ? `${money(summary.quoted_per_or)} / OR` : null,
          summary.quoted_per_chair != null ? `${money(summary.quoted_per_chair)} / chair` : null].filter(Boolean).join(' · ')}
      </div>
      {row(added ? 'Margin, nightly cleaning' : 'Margin', pct(summary.margin_assumed, 1))}
      {row('vs what they pay', summary.vs_incumbent_pct != null ? `${Number(summary.vs_incumbent_pct) >= 0 ? '+' : ''}${pct(summary.vs_incumbent_pct)}` : 'not known')}
      {row('Below floor?', bid.floor_monthly == null ? 'no floor set' : summary.below_floor ? 'Yes — executive only' : 'No')}
      {row('Above top?', summary.above_top ? 'Yes — allowed, and noted' : 'No')}
      {row('AM review', bid.am_reviewed_by_eid ? '✓' : 'not yet')}
    </div>
  );
}

export function ReviewPanel({ bid, isExecutive }: { bid: BidRow; isExecutive: boolean }) {
  const { data: summary, loading } = useBidLoad<QuoteSummary | null>(() => fetchQuoteSummary(bid.bid_id), [bid.bid_id, bid.updated_at], null, 5000);
  const [sendBack, setSendBack] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const locked = bid.status === 'Approved' || bid.status === 'Won' || bid.status === 'Lost' || bid.status === 'Expired' || bid.status === 'Withdrawn';

  async function act(fields: Parameters<typeof updateBid>[1], label: string) {
    setBusy(true); setErr(null);
    try { await updateBid(bid.bid_id, fields, label); setSendBack(false); setNote(''); } catch (e) { setErr(saveText(e)); } finally { setBusy(false); }
  }

  const saved = useSavedPrice(bid);
  const requestReason = !bid.am_reviewed_by_eid
    ? 'Name the account manager who checked this bid, and what they caught, first.'
    : bid.quoted_monthly == null ? 'Price the bid first.'
    : saved.data?.view.errors.length ? 'Finish step 3, Services, first.' : null;

  return (
    <div className="space-y-4">
      <AmReviewCard bid={bid} locked={locked} />
      {bid.status === 'Draft' && bid.executive_approval_notes && (
        <div className="rounded-lg border border-st-warn-border bg-st-warn-bg p-3 text-sm text-st-warn-fg-strong">
          <b>Sent back for more information:</b> {bid.executive_approval_notes}
        </div>
      )}
      {bid.status === 'Approved' && (
        <div className="rounded-lg border border-st-ok-border bg-st-ok-bg p-3 text-sm text-st-ok-fg">
          Pricing approved {shortDate(bid.executive_approved_at)}{bid.executive_approval_notes ? ` — ${bid.executive_approval_notes}` : ''}. The price is locked. Documents and sending are coming with the proposal step.
        </div>
      )}
      <SaveError text={err} />
      {loading || !summary ? <Skeleton className="h-48 w-full" /> : (
        <div className="grid gap-4 lg:grid-cols-[1fr_260px]">
          <DialIn bid={bid} summary={summary} locked={locked} />
          <div className="space-y-3">
            <Summary bid={bid} summary={summary} price={saved.data?.price ?? null} />
            {bid.status === 'Draft' && (
              <div className="space-y-1">
                <Button className="w-full" disabled={!!requestReason} loading={busy} onClick={() => act({ status: 'Submitted' }, 'request executive review')}>Request executive review</Button>
                {requestReason && <p className="text-xs text-tcs-text-muted">{requestReason}</p>}
              </div>
            )}
            {bid.status === 'Submitted' && !isExecutive && (
              <p className="text-sm text-tcs-text-muted">Waiting for an executive to approve the pricing or send it back.</p>
            )}
            {bid.status === 'Submitted' && isExecutive && (
              <div className="space-y-2">
                <Button className="w-full" loading={busy} onClick={() => act({ status: 'Approved' }, 'approve the pricing')}>Approve pricing</Button>
                {!sendBack ? (
                  <Button className="w-full" variant="secondary" onClick={() => setSendBack(true)}>Send back — need more info</Button>
                ) : (
                  <div className="space-y-2">
                    <Field label="What more is needed?"><textarea className={INPUT} rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setSendBack(false)}>Cancel</Button>
                      <Button size="sm" disabled={!note.trim()} loading={busy} onClick={() => act({ status: 'Draft', executive_approval_notes: note.trim() }, 'send the bid back')}>Send back</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {bid.status === 'Submitted' && !isExecutive && (
              <Button className="w-full" variant="ghost" size="sm" loading={busy} onClick={() => act({ status: 'Withdrawn' }, 'withdraw the bid')}>Withdraw the bid</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
